import { format, parse, addMinutes, differenceInMinutes, isWithinInterval, startOfDay, endOfDay, addDays, isSameDay } from 'date-fns';
import { CALENDAR_SETTINGS, TIME_FORMAT, DATE_FORMAT, DATETIME_FORMAT } from './constants';

/**
 * 時間スロットの配列を生成
 * @returns {Array} 時間スロット配列 (例: ['07:00', '07:15', ...])
 */
export const generateTimeSlots = () => {
  const slots = [];
  const { START_HOUR, END_HOUR, TIME_SLOT_MINUTES } = CALENDAR_SETTINGS;

  for (let hour = START_HOUR; hour <= END_HOUR; hour++) {
    for (let minute = 0; minute < 60; minute += TIME_SLOT_MINUTES) {
      const timeString = `${hour.toString().padStart(2, '0')}:${minute.toString().padStart(2, '0')}`;
      slots.push(timeString);

      // 終了時刻に達したら終了
      if (hour === END_HOUR && minute === 0) break;
    }
  }

  return slots;
};

/**
 * 15分単位の時間オプションを生成（select用）
 * @returns {Array} 時間オプション配列 (例: [{value: '07:00', label: '07:00'}, ...])
 */
export const generateTimeOptions = () => {
  const options = [];
  const { START_HOUR, END_HOUR } = CALENDAR_SETTINGS;

  for (let hour = START_HOUR; hour <= END_HOUR; hour++) {
    for (let minute = 0; minute < 60; minute += 15) {
      const timeString = `${hour.toString().padStart(2, '0')}:${minute.toString().padStart(2, '0')}`;
      options.push({ value: timeString, label: timeString });

      // 終了時刻に達したら終了
      if (hour === END_HOUR && minute === 0) break;
    }
  }

  return options;
};

/**
 * 30分単位の時間オプションを生成（select用）
 * @returns {Array} 時間オプション配列 (例: [{value: '07:00', label: '07:00'}, ...])
 */
export const generateTimeOptions30Min = () => {
  const options = [];
  const { START_HOUR, END_HOUR } = CALENDAR_SETTINGS;

  for (let hour = START_HOUR; hour <= END_HOUR; hour++) {
    for (let minute = 0; minute < 60; minute += 30) {
      const timeString = `${hour.toString().padStart(2, '0')}:${minute.toString().padStart(2, '0')}`;
      options.push({ value: timeString, label: timeString });

      // 終了時刻に達したら終了
      if (hour === END_HOUR && minute === 0) break;
    }
  }

  return options;
};

/**
 * 日付と時刻文字列からDateオブジェクトを生成
 * @param {Date} date - 日付
 * @param {string} timeString - 時刻文字列 (例: '09:30')
 * @returns {Date}
 */
export const combineDateTime = (date, timeString) => {
  const [hours, minutes] = timeString.split(':').map(Number);
  const newDate = new Date(date);
  newDate.setHours(hours, minutes, 0, 0);
  return newDate;
};

/**
 * Dateオブジェクトを時刻文字列に変換
 * @param {Date} date
 * @returns {string} 時刻文字列 (例: '09:30')
 */
export const formatTime = (date) => {
  return format(date, TIME_FORMAT);
};

/**
 * Dateオブジェクトを日付文字列に変換
 * @param {Date} date
 * @returns {string} 日付文字列 (例: '2025-10-21')
 */
export const formatDate = (date) => {
  return format(date, DATE_FORMAT);
};

/**
 * Dateオブジェクトを日時文字列に変換
 * @param {Date} date
 * @returns {string} 日時文字列 (例: '2025-10-21 09:30')
 */
export const formatDateTime = (date) => {
  return format(date, DATETIME_FORMAT);
};

/**
 * 時刻文字列をDateオブジェクトにパース
 * @param {string} timeString - 時刻文字列 (例: '09:30')
 * @param {Date} referenceDate - 基準日（デフォルトは今日）
 * @returns {Date}
 */
export const parseTime = (timeString, referenceDate = new Date()) => {
  return parse(timeString, TIME_FORMAT, referenceDate);
};

/**
 * 2つの時刻の差を分単位で計算
 * @param {Date} start
 * @param {Date} end
 * @returns {number} 分単位の差
 */
export const getMinutesDifference = (start, end) => {
  return differenceInMinutes(end, start);
};

/**
 * 指定時刻から指定分数後の時刻を計算
 * @param {Date} time
 * @param {number} minutes
 * @returns {Date}
 */
export const addMinutesToTime = (time, minutes) => {
  return addMinutes(time, minutes);
};

/**
 * 時刻が15分単位かチェック
 * @param {Date} date
 * @returns {boolean}
 */
export const isQuarterHour = (date) => {
  return date.getMinutes() % 15 === 0;
};

/**
 * 時刻を15分単位に丸める（切り捨て）
 * @param {Date} date
 * @returns {Date}
 */
export const roundToQuarterHour = (date) => {
  const minutes = date.getMinutes();
  const roundedMinutes = Math.floor(minutes / 15) * 15;
  const newDate = new Date(date);
  newDate.setMinutes(roundedMinutes, 0, 0);
  return newDate;
};

/**
 * 時刻を15分単位に丸める（切り上げ）
 * @param {Date} date
 * @returns {Date}
 */
export const roundUpToQuarterHour = (date) => {
  const minutes = date.getMinutes();
  const roundedMinutes = Math.ceil(minutes / 15) * 15;
  const newDate = new Date(date);
  newDate.setMinutes(roundedMinutes, 0, 0);
  return newDate;
};

/**
 * 時刻を1時間単位で四捨五入
 * @param {Date} date
 * @returns {Date}
 */
export const roundToHour = (date) => {
  const minutes = date.getMinutes();
  const seconds = date.getSeconds();
  const newDate = new Date(date);

  // 30分以上なら1時間繰り上げ、30分未満なら切り捨て
  if (minutes >= 30 || (minutes === 0 && seconds === 0)) {
    newDate.setHours(newDate.getHours() + 1, 0, 0, 0);
  } else {
    newDate.setMinutes(0, 0, 0);
  }

  return newDate;
};

/**
 * 予約がカレンダーの表示範囲内かチェック
 * @param {Date} reservationStart
 * @param {Date} reservationEnd
 * @param {Date} calendarDate
 * @returns {boolean}
 */
export const isReservationInDay = (reservationStart, reservationEnd, calendarDate) => {
  const dayStart = startOfDay(calendarDate);
  const dayEnd = endOfDay(calendarDate);

  return (
    isWithinInterval(reservationStart, { start: dayStart, end: dayEnd }) ||
    isWithinInterval(reservationEnd, { start: dayStart, end: dayEnd }) ||
    (reservationStart < dayStart && reservationEnd > dayEnd)
  );
};

/**
 * 予約ブロックの表示位置を計算（top、height）
 * @param {Date} startTime - 予約開始時刻
 * @param {Date} endTime - 予約終了時刻
 * @param {Date} calendarDate - カレンダーの日付
 * @returns {Object} { top: number, height: number } (ピクセル単位)
 */
export const calculateBlockPosition = (startTime, endTime, calendarDate) => {
  const { START_HOUR, GRID_ROW_HEIGHT } = CALENDAR_SETTINGS;

  // カレンダーの開始時刻
  const calendarStart = new Date(calendarDate);
  calendarStart.setHours(START_HOUR, 0, 0, 0);

  // 開始位置を計算（カレンダー開始時刻からの経過分）
  const minutesFromStart = differenceInMinutes(startTime, calendarStart);
  const top = (minutesFromStart / 60) * GRID_ROW_HEIGHT - 40; // 40ピクセル上にずらす

  // 高さを計算（予約時間の長さ）
  const durationMinutes = differenceInMinutes(endTime, startTime);
  const height = (durationMinutes / 60) * GRID_ROW_HEIGHT;

  return { top, height };
};

/**
 * マウス位置から時刻を計算
 * @param {number} offsetY - カレンダーグリッド上のY座標
 * @param {Date} calendarDate - カレンダーの日付
 * @returns {Date}
 */
export const getTimeFromPosition = (offsetY, calendarDate) => {
  const { START_HOUR, GRID_ROW_HEIGHT } = CALENDAR_SETTINGS;

  // カレンダーの開始時刻
  const calendarStart = new Date(calendarDate);
  calendarStart.setHours(START_HOUR, 0, 0, 0);

  // Y座標から経過時間（分）を計算
  const minutesFromStart = (offsetY / GRID_ROW_HEIGHT) * 60;

  // 15分単位に丸める
  const time = addMinutes(calendarStart, minutesFromStart);
  return roundToQuarterHour(time);
};

/**
 * 今日の日付を取得
 * @returns {Date}
 */
export const getToday = () => {
  return new Date();
};

/**
 * 週の開始日（日曜日）を取得
 * @param {Date} date
 * @returns {Date}
 */
export const getWeekStart = (date) => {
  const day = date.getDay();
  const diff = -day; // 日曜日(0)ならそのまま、月曜(1)なら-1日、...土曜(6)なら-6日
  return addDays(date, diff);
};

/**
 * 2つの時刻が重複しているかチェック
 * @param {Date} start1
 * @param {Date} end1
 * @param {Date} start2
 * @param {Date} end2
 * @returns {boolean}
 */
export const hasTimeOverlap = (start1, end1, start2, end2) => {
  return start1 < end2 && end1 > start2;
};

/**
 * ISO文字列をDateオブジェクトに変換（ローカルタイムとして）
 * @param {string} isoString - 'YYYY-MM-DDTHH:mm:ss' 形式の文字列
 * @returns {Date}
 */
export const parseISOString = (isoString) => {
  // タイムゾーン変換を避けるため、文字列を明示的にパース
  const [datePart, timePart] = isoString.split('T');
  const [year, month, day] = datePart.split('-').map(Number);
  const [hours, minutes, seconds] = (timePart || '00:00:00').split(':').map(Number);

  // ローカルタイムとして Date オブジェクトを作成（月は0から始まるので-1）
  return new Date(year, month - 1, day, hours, minutes, seconds || 0);
};

/**
 * 営業時間内かチェック
 * @param {Date} time
 * @returns {boolean}
 */
export const isWithinBusinessHours = (time) => {
  const hour = time.getHours();
  const { START_HOUR, END_HOUR } = CALENDAR_SETTINGS;
  return hour >= START_HOUR && hour < END_HOUR;
};

/**
 * ローカル時刻をISO形式の文字列に変換（タイムゾーンオフセットなし）
 * @param {Date} date
 * @returns {string} YYYY-MM-DDTHH:mm:ss 形式の文字列
 */
export const formatLocalDateTime = (date) => {
  const year = date.getFullYear();
  const month = String(date.getMonth() + 1).padStart(2, '0');
  const day = String(date.getDate()).padStart(2, '0');
  const hours = String(date.getHours()).padStart(2, '0');
  const minutes = String(date.getMinutes()).padStart(2, '0');
  const seconds = String(date.getSeconds()).padStart(2, '0');
  return `${year}-${month}-${day}T${hours}:${minutes}:${seconds}`;
};
